import { useEffect, useState } from "react";
import { Link } from "wouter";
import { IdCard, Printer, UtensilsCrossed } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { UltraBadge } from "@/components/UltraBadge";

export const MAX_TABLES = 999;

type Mudanca = { tableOrdersEnabled?: boolean; tableCount?: number; tableQrPerClient?: boolean; tablePrintFullBill?: boolean; tableQrOnly?: boolean; tableShowClosed?: boolean };

/**
 * Configurações → os QR codes da loja (Ultra), em dois cartões (Pedro, 29/10/2026):
 * - «Pedido na mesa»: um QR em cada mesa; quem está na mesa pede pelo celular e tudo entra na conta da mesa;
 * - «Comanda individual»: cada cliente recebe o seu QR (a comanda), entra com nome e WhatsApp, e a comanda fica com
 *   ele até a conta fechar (no banco, `table_qr_per_client`).
 * Os dois usam os mesmos QR codes: ligar um desliga o outro. Desligar ou trocar pede confirmação.
 */
export function TableOrdersSection({
  modo,
  enabled,
  porCliente,
  count,
  contaCompleta,
  imprimeConta = false,
  soPeloQr,
  mostrarFechadas = true,
  podeUsar,
  mostrarSelo,
  onChange,
}: {
  modo: "mesa" | "comanda";
  enabled: boolean;
  porCliente: boolean;
  count: number | null;
  contaCompleta: boolean;
  /** «Imprimir quando o cliente pedir a conta» (Impressão automática); desligado, a escolha do papel não vale. */
  imprimeConta?: boolean;
  /** «Link público apenas pela Mesa/Comanda»: o link sem o QR code só mostra o «Ler QR code». */
  soPeloQr: boolean;
  /** «Mostrar comandas fechadas» (Pedro, 01/11/2026, A-0065; ligado por padrão): o filtro «Fechadas» da página. */
  mostrarFechadas?: boolean;
  podeUsar: boolean;
  mostrarSelo: boolean;
  onChange: (patch: Mudanca) => void;
}) {
  const comanda = modo === "comanda";
  const ativo = enabled && porCliente === comanda;
  const outroAtivo = enabled && porCliente !== comanda;
  const confirm = useConfirm();
  const nome = (c: boolean) => (c ? "a comanda individual" : "o pedido na mesa");
  // Desligar, ou ligar o outro (que desliga este), pede confirmação (Pedro, 31/10/2026: um clique sem querer desligou
  // a comanda individual da Palco 47 no meio do serviço). As comandas não perdem o vínculo; o aviso não fala disso.
  const mudar = async (v: boolean) => {
    if (!v || outroAtivo) {
      const ok = await confirm(
        v
          ? { title: `Trocar para ${nome(comanda)}?`, description: comanda ? "O pedido na mesa será desligado." : "A comanda individual será desligada.", confirmLabel: "Trocar" }
          : { title: `Desligar ${nome(comanda)}?`, confirmLabel: "Desligar" },
      );
      if (!ok) return;
    }
    onChange(
      v
        ? { tableOrdersEnabled: true, tableQrPerClient: comanda, ...(!count ? { tableCount: 10 } : {}) }
        : { tableOrdersEnabled: false, tableQrPerClient: false },
    );
  };
  // Texto cru enquanto digita: apagar pra redigitar não pode gravar 0.
  const [countText, setCountText] = useState(count ? String(count) : "");
  useEffect(() => setCountText(count ? String(count) : ""), [count]);

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2 text-lg font-semibold flex-wrap">
        {comanda ? <IdCard className="w-5 h-5 text-primary" /> : <UtensilsCrossed className="w-5 h-5 text-primary" />}
        {comanda ? "Comanda individual" : "Pedido na mesa"}
        {mostrarSelo && <UltraBadge />}
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <Label className="text-sm font-medium">{comanda ? "Cada cliente com a sua comanda" : "Cliente pede pelo QR code da mesa"}</Label>
          <p className="text-xs text-muted-foreground">
            {comanda
              ? "Cada cliente recebe um QR code, a comanda. Ele entra com nome e WhatsApp e pede pelo celular. A comanda fica com ele até você fechar a conta no PDV."
              : "Um QR code em cada mesa. Quem está na mesa pede pelo celular, e os pedidos entram na conta da mesa, que você fecha no PDV."}
          </p>
          {outroAtivo && (
            <p className="text-xs font-medium text-muted-foreground">
              Ligar aqui desliga {comanda ? "o pedido na mesa" : "a comanda individual"}.
            </p>
          )}
        </div>
        <Switch
          className="mt-0.5 shrink-0"
          checked={ativo}
          disabled={!ativo && !podeUsar}
          onCheckedChange={(v) => void mudar(v)}
          data-testid={comanda ? "settings-comanda" : "settings-table-orders"}
        />
      </div>

      {ativo && (
        <div className="rounded-xl border bg-muted/30 p-4 space-y-4">
          {/* Pedro, 31/10/2026: o link da loja só pelo QR code (o mesmo campo nos dois modos; o nome segue o que está ligado). */}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <Label className="text-sm font-normal" htmlFor={`settings-${modo}-so-pelo-qr`}>
                {comanda ? "Link público apenas pela Comanda" : "Link público apenas pela Mesa"}
              </Label>
              <p className="text-xs text-muted-foreground">
                Quem abrir o link da loja sem o QR code vê só o botão «Ler QR code {comanda ? "da Comanda" : "da Mesa"}».
              </p>
            </div>
            <Switch
              id={`settings-${modo}-so-pelo-qr`}
              className="mt-0.5 shrink-0"
              checked={soPeloQr}
              onCheckedChange={(v) => onChange({ tableQrOnly: v })}
              data-testid="settings-so-pelo-qr"
            />
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <Label className="text-sm font-normal" htmlFor={`settings-${modo}-mostrar-fechadas`}>
                {comanda ? "Mostrar comandas fechadas" : "Mostrar mesas fechadas"}
              </Label>
              <p className="text-xs text-muted-foreground">
                O filtro «Fechadas» na página {comanda ? "Comandas" : "Mesas"}, para imprimir de novo uma conta já fechada (últimos 7 dias).
              </p>
            </div>
            <Switch
              id={`settings-${modo}-mostrar-fechadas`}
              className="mt-0.5 shrink-0"
              checked={mostrarFechadas}
              onCheckedChange={(v) => onChange({ tableShowClosed: v })}
              data-testid="settings-mostrar-fechadas"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label className="text-sm font-normal" htmlFor={`settings-${modo}-count`}>
              {comanda ? "Quantas comandas" : "Quantas mesas"}
            </Label>
            <Input
              id={`settings-${modo}-count`}
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_TABLES}
              className="w-24 h-10 text-right bg-background"
              value={countText}
              onChange={(e) => {
                setCountText(e.target.value);
                const n = Math.trunc(Number(e.target.value));
                if (n >= 1 && n <= MAX_TABLES) onChange({ tableCount: n });
              }}
              data-testid="settings-table-count"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-normal">Quando o cliente pedir a conta, a impressora imprime</Label>
            {!imprimeConta && (
              <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="settings-conta-pedida-desligada">
                Desligado em «Impressão automática»: o pedido de conta só avisa no celular da loja.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { valor: true, titulo: "A conta completa", texto: comanda ? "Os pedidos da comanda e o total" : "Os pedidos da mesa e o total" },
                { valor: false, titulo: "Só o aviso", texto: comanda ? "O nome e o WhatsApp de quem pediu" : "«A mesa 6 pediu a conta»" },
              ].map((o) => (
                <button
                  key={String(o.valor)}
                  type="button"
                  onClick={() => onChange({ tablePrintFullBill: o.valor })}
                  aria-pressed={contaCompleta === o.valor}
                  className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                    contaCompleta === o.valor ? "border-primary bg-primary/10" : "bg-background hover:bg-muted"
                  }`}
                  data-testid={`settings-conta-${o.valor ? "completa" : "aviso"}`}
                >
                  <span className="block text-sm font-medium">{o.titulo}</span>
                  <span className="block text-xs text-muted-foreground">{o.texto}</span>
                </button>
              ))}
            </div>
          </div>
          <Button type="button" variant="outline" className="w-full sm:w-auto bg-background" asChild>
            <Link href="/configuracoes/mesas" data-testid="settings-table-qrcodes">
              <Printer className="w-4 h-4 mr-2" /> {comanda ? "Imprimir as comandas" : "Imprimir QR codes das mesas"}
            </Link>
          </Button>
        </div>
      )}
    </Card>
  );
}
